import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { MdArrowForward, MdChevronLeft, MdChevronRight, MdOutlinePlayArrow } from "react-icons/md";
import { useLocation, useParams } from "react-router-dom";

import transcript from "../../../assets/json/meB5peyf4Mim.vtt";
import playBtnYellow from "../../../assets/playBtn_yellow.svg";
import transcriptIcon from "../../../assets/transcript.svg";
import WhiteCurl from "../../../assets/white_curl.svg";
import { USER_TYPE } from "../../../constants/common";
import lessonServices from "../../../services/lessonServices";
import { getLanguage } from "../../../utils/getLanguage";
import { useNavigateWithLang } from "../../../utils/navigation";

const StudentLessonVideo = () => {
  const { id: lessonID, lessonType } = useParams();
  const location = useLocation();
  const { t } = useTranslation();

  const lessonName = location.state?.lessonName;

  const lang = getLanguage();
  const navigate = useNavigateWithLang();
  const videoRef = useRef(null);
  const cueRefs = useRef([]);

  const [isPlaying, setIsPlaying] = useState(false);
  const [thumbnail, setThumbnail] = useState("");
  const [activeVideo, setActiveVideo] = useState(null);
  const [lessonVideo, setLessonVideo] = useState(null);
  const [recapVideo, setRecapVideo] = useState(null);
  const [showTranscript, setShowTranscript] = useState(false);
  const [isRecapViewed, setIsRecapViewed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [activeIndex, setActiveIndex] = useState(null);
  const [vttCues, setVttCues] = useState([]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (videoRef.current && !videoRef.current.paused) {
        setCurrentTime(videoRef.current.currentTime);
      }
    }, 300);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    cueRefs.current = vttCues.map(() => React.createRef());
  }, [vttCues]);

  useEffect(() => {
    const index = vttCues.findIndex((line) => currentTime >= line.start && currentTime < line.end);
    setActiveIndex(index);
  }, [currentTime, vttCues]);

  useEffect(() => {
    if (activeIndex !== null && cueRefs.current[activeIndex]) {
      cueRefs.current[activeIndex].current?.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    }
  }, [activeIndex]);

  useEffect(() => {
    const track = videoRef.current?.textTracks?.[0];

    if (track) {
      track.mode = "hidden"; // Ensure cues are available

      const extractCues = () => {
        if (track.cues) {
          const cues = Array.from(track.cues).map((cue) => ({
            start: cue.startTime,
            end: cue.endTime,
            text: cue.text,
          }));
          setVttCues(cues);
        }
      };

      // Wait if cues are not loaded immediately
      if (track.cues && track.cues.length > 0) {
        extractCues();
      } else {
        const onCueChange = () => {
          extractCues();
          track.removeEventListener("cuechange", onCueChange);
        };
        track.addEventListener("cuechange", onCueChange);
      }
    }
  }, [isPlaying]);

  function getThumbnail(videoUrl, timeInSeconds = 1) {
    return new Promise((resolve, reject) => {
      const video = document.createElement("video");
      video.crossOrigin = "anonymous";
      video.src = videoUrl;
      video.preload = "metadata";

      video.addEventListener("loadeddata", () => {
        video.currentTime = timeInSeconds;
      });

      video.addEventListener("seeked", () => {
        const canvas = document.createElement("canvas");
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL("image/jpeg"));
      });

      video.onerror = () => reject("Failed to load video");
    });
  }

  const handleRecapClick = async () => {
    if (!recapVideo?.video) return;
    setActiveVideo(recapVideo);
    setIsPlaying(true);
    const thumb = await getThumbnail(recapVideo.video, 1);
    setThumbnail(thumb);
  };

  const handleVideoEnd = async () => {
    if (activeVideo?.type === "recap") {
      await addViewRecap();

      if (lessonVideo?.video) {
        setActiveVideo(lessonVideo);
        setIsPlaying(true);
      }
    } else if (activeVideo?.type === "lesson") {
      await lessonServices.takeWatchVideoPoints(lessonID);
    }
  };

  const addViewRecap = async () => {
    try {
      await lessonServices.addViewRecap(lessonID);
      setIsRecapViewed(true);
    } catch (error) {
      console.error("Error adding view recap:", error);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      try {
        const lessonRes = await lessonServices.getVideo(lessonID, "lesson");
        lessonRes.type = "lesson";
        setLessonVideo(lessonRes);

        let viewed = false;
        let recapRes = null;

        try {
          const recapStatus = await lessonServices.getIsRecapVideoViewed(lessonID);
          viewed = !!recapStatus?.is_viewed;

          const recapVideoRes = await lessonServices.getVideo(lessonID, "recap");
          if (recapVideoRes?.video) {
            recapRes = recapVideoRes;
            recapRes.type = "recap";
            setRecapVideo(recapRes);
          }
        } catch (err) {
          console.error("Error checking recap viewed status:", err);
        }

        setIsRecapViewed(viewed);

        const defaultVideo = !viewed && recapRes ? recapRes : lessonRes;
        setActiveVideo(defaultVideo);

        if (defaultVideo?.video) {
          const thumb = await getThumbnail(defaultVideo.video, 1);
          setThumbnail(thumb);
        }
      } catch (error) {
        console.error("Error loading video data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [lang]);

  return (
    <div
      className={`grid ${showTranscript ? "" : "mx-auto w-[65%]"} gap-6 ${
        showTranscript ? "grid-cols-6" : "grid-cols-1"
      }`}
    >
      <div className="col-span-4 flex flex-col space-y-2">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-blue-500">{lessonName}</h1>

          {/* <button
            onClick={() => setShowTranscript(!showTranscript)}
            className="flex gap-2 rounded-md p-1 px-2 text-sm font-semibold text-blue-600 underline transition-all duration-300 hover:bg-blue-100"
          >
            {showTranscript ? <MdChevronRight size={25} /> : <MdChevronLeft size={25} />}
            <img src={transcriptIcon} className="mt-1 w-6" />
          </button> */}
        </div>

        <div className="relative aspect-video w-full overflow-hidden rounded-3xl border-2 border-[#96B8F3]">
          {recapVideo?.video && activeVideo?.type !== "recap" && (
            <div
              className="absolute left-3 top-3 z-10 flex cursor-pointer rounded-full bg-blue-500 px-2 py-1 text-xs text-white hover:bg-blue-600"
              onClick={handleRecapClick}
            >
              <span>{t("recap_video")}</span>
              <MdOutlinePlayArrow size={18} />
            </div>
          )}

          {!isPlaying ? (
            <div
              className="group relative h-full w-full cursor-pointer overflow-hidden"
              onClick={() => setIsPlaying(true)}
            >
              <img
                src={activeVideo?.thumbnail || thumbnail}
                alt="Video Thumbnail"
                className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
              />
              <div className="absolute inset-0 flex items-center justify-center bg-blue-200/30 backdrop-blur-sm transition-opacity duration-300 group-hover:bg-blue-200/50">
                <div className="rounded-full transition-transform duration-300 group-hover:scale-110">
                  <img src={playBtnYellow} alt="Play Button" />
                </div>
              </div>
            </div>
          ) : (
            <video
              ref={videoRef}
              src={activeVideo?.video}
              controls
              autoPlay
              controlsList="nodownload"
              className="h-full w-full"
              onEnded={handleVideoEnd}
            >
              {/* <track src={transcript} /> */}
              Your browser does not support the video tag.
            </video>
          )}
        </div>

        <div className="flex flex-1 flex-col">
          <button
            onClick={() => {
              const basePath =
                lessonType === "code"
                  ? `/${USER_TYPE.STUDENT.ROLE}/coding-area/code/${lessonID}`
                  : `/${USER_TYPE.STUDENT.ROLE}/learning-area/lesson-questions/${lessonID}`;
              navigate(basePath, {}, { lessonName });
            }}
            className="wavy-btn relative m-auto mt-1 min-w-72 flex-1 rounded-3xl border-4 border-[#96B8F3] bg-blue-500 p-4 text-lg font-semibold text-white transition duration-200 hover:bg-blue-600"
          >
            <span className="absolute left-[6px] top-[6px]">
              <img src={WhiteCurl} className="w-5" alt="" />
            </span>
            {t("next")}
          </button>

          {lessonType === "code" && (
            <button
              onClick={() =>
                navigate(
                  `/${USER_TYPE.STUDENT.ROLE}/learning-area/lesson-questions/${lessonID}`,
                  {},
                  { lessonName }
                )
              }
              className="mt-2 flex flex-1 items-center justify-center gap-2 font-semibold text-orange-500 underline"
            >
              <span>{t("skip_to_questions")}</span>
              <MdArrowForward className="mt-1 rtl:rotate-180" size={22} />
            </button>
          )}
        </div>
      </div>

      {showTranscript && (
        <div className="animate-slide-in-right custom-scrollbar col-span-2 max-h-[500px] overflow-y-auto px-3">
          <h2 className="mb-6 flex items-center gap-2 text-2xl font-bold text-blue-500">
            Script & Description
          </h2>
          <div className="space-y-3 text-sm text-gray-700">
            {vttCues.map((line, index) => (
              <p
                key={index}
                ref={cueRefs.current[index]}
                onClick={() => {
                  if (videoRef.current) {
                    videoRef.current.currentTime = line.start;
                    videoRef.current.play();
                  }
                }}
                className={`cursor-pointer rounded px-2 py-1 transition ${
                  index === activeIndex ? "bg-yellow-100 font-bold text-black" : "hover:bg-gray-100"
                }`}
              >
                {line.text}
              </p>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentLessonVideo;
